import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useSearch } from '@tanstack/react-router'
import { useEffect, useMemo, useRef, useState } from 'react'
import { ApiError, type components } from '@akillidernek/api-client'
import { Badge, Button, Field, Icon, Input, useToast } from '@akillidernek/ui'
import { api } from '../api'
import { useTenantSlug } from '../use-tenant'
import { BLOK_ALANLARI, DINAMIK_ACIKLAMA, DINAMIK_ALANLAR, type Alan } from './web-sitesi-alanlar'

type Sayfa = components['schemas']['PageRead']
type Ayarlar = components['schemas']['SettingsRead']
type Blok = { id: string; type: string; hidden: boolean; data: Record<string, unknown> }

const GRUP_ADI: Record<string, string> = {
  basic: 'Temel',
  association: 'Dernek',
  other: 'Diğer',
}

const girdiSinifi =
  'h-9 w-full rounded-[var(--radius-control)] border border-[var(--ad-border)] bg-[var(--ad-surface)] px-2 text-sm'

/**
 * Blok düzenleyici — YES-185 (Figma: `Panel · Web Sitesi / Düzenleyici`).
 *
 * Üç bölme: solda blok paleti, ortada sayfanın blokları, sağda seçili bloğun
 * alanları. Otomatik kayıt var ama YAYIN ayrı bir adım: taslak ile yayınlanmış
 * anlık görüntü sunucuda da ayrı duruyor (modules/content/models.py).
 *
 * Tuvalde sitenin birebir görüntüsü YOK, blokların yapısı var. Sitenin
 * bileşenleri Astro'da (ADR-15); onları panelde ikinci kez yazmak, iki
 * uygulamanın sessizce ayrışması demekti. "Önizle" gerçek siteyi açıyor.
 */
export function WebSitesiDuzenleyici() {
  const slug = useTenantSlug()
  const toast = useToast()
  const queryClient = useQueryClient()
  const { sayfa: istenenSayfa } = useSearch({ from: '/web-sitesi/duzenleyici' })

  const [aktifSayfaId, setAktifSayfaId] = useState<string | null>(istenenSayfa ?? null)
  const [bloklar, setBloklar] = useState<Blok[]>([])
  const [seciliBlok, setSeciliBlok] = useState<string | null>(null)
  const [kirli, setKirli] = useState(false)
  const [sekme, setSekme] = useState<'blok' | 'tema' | 'menu' | 'seo'>('blok')
  const [sonKayit, setSonKayit] = useState<Date | null>(null)
  const yuklenenSayfa = useRef<string | null>(null)

  const site = useQuery({
    queryKey: ['site', slug],
    enabled: Boolean(slug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/site')
      return data
    },
  })

  const katalog = useQuery({
    queryKey: ['site-blocks', slug],
    enabled: Boolean(slug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/site/blocks')
      return data ?? []
    },
  })

  const sayfalar = useMemo(
    () => [...(site.data?.pages ?? [])].sort((a, b) => a.menu_order - b.menu_order),
    [site.data],
  )
  const aktifSayfa: Sayfa | undefined =
    sayfalar.find((s) => s.id === aktifSayfaId) ?? sayfalar.find((s) => s.kind !== 'auto')

  // Sayfa değişince blokları sunucudan tazele. `useRef`: aynı sayfa için
  // yeniden yüklemek, kullanıcının yazdıklarını geri alırdı.
  useEffect(() => {
    if (!aktifSayfa || yuklenenSayfa.current === aktifSayfa.id) return
    yuklenenSayfa.current = aktifSayfa.id
    setAktifSayfaId(aktifSayfa.id)
    setBloklar(aktifSayfa.blocks as Blok[])
    setSeciliBlok(null)
    setKirli(false)
  }, [aktifSayfa])

  const kaydet = useMutation({
    mutationFn: async (yeniBloklar: Blok[]) => {
      if (!aktifSayfa) return
      await api.PATCH('/api/v1/site/pages/{page_id}', {
        params: { path: { page_id: aktifSayfa.id } },
        body: { blocks: yeniBloklar },
      })
    },
    onSuccess: () => {
      setKirli(false)
      setSonKayit(new Date())
      void queryClient.invalidateQueries({ queryKey: ['site', slug] })
    },
    onError: (error) =>
      toast({
        title: 'Kaydedilemedi',
        description: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'error',
      }),
  })

  // Otomatik kayıt: yazmayı bıraktıktan bir saniye sonra. Her tuş vuruşunda
  // istek atmak, uzun bir metinde yüzlerce istek demekti.
  useEffect(() => {
    if (!kirli) return
    const zamanlayici = setTimeout(() => kaydet.mutate(bloklar), 1000)
    return () => clearTimeout(zamanlayici)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kirli, bloklar])

  const yayinla = useMutation({
    mutationFn: async () => {
      if (!aktifSayfa) return
      if (kirli) await kaydet.mutateAsync(bloklar)
      await api.POST('/api/v1/site/pages/{page_id}/publish', {
        params: { path: { page_id: aktifSayfa.id } },
      })
    },
    onSuccess: () => {
      toast({ title: 'Sayfa yayınlandı', tone: 'success' })
      void queryClient.invalidateQueries({ queryKey: ['site', slug] })
    },
    onError: (error) =>
      toast({
        title: 'Yayınlanamadı',
        description: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'error',
      }),
  })

  const temaKaydet = useMutation({
    mutationFn: async (govde: Record<string, unknown>) => {
      await api.PATCH('/api/v1/site/settings', { body: govde })
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['site', slug] }),
    onError: (error) =>
      toast({
        title: 'Tema kaydedilemedi',
        description: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'error',
      }),
  })

  const sayfaKaydet = useMutation({
    mutationFn: async ({ id, ...govde }: { id: string } & Record<string, unknown>) => {
      await api.PATCH('/api/v1/site/pages/{page_id}', {
        params: { path: { page_id: id } },
        body: govde,
      })
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['site', slug] }),
    onError: (error) =>
      toast({
        title: 'Kaydedilemedi',
        description: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'error',
      }),
  })

  function bloklariDegistir(sonraki: Blok[]) {
    setBloklar(sonraki)
    setKirli(true)
  }

  function blokEkle(tur: string) {
    const yeni: Blok = { id: gecicikimlik(bloklar), type: tur, hidden: false, data: {} }
    bloklariDegistir([...bloklar, yeni])
    setSeciliBlok(yeni.id)
  }

  function blokGuncelle(id: string, veri: Record<string, unknown>) {
    bloklariDegistir(bloklar.map((b) => (b.id === id ? { ...b, data: veri } : b)))
  }

  function blokTasi(id: string, yon: -1 | 1) {
    const yer = bloklar.findIndex((b) => b.id === id)
    const hedef = yer + yon
    if (hedef < 0 || hedef >= bloklar.length) return
    const kopya = [...bloklar]
    const [tasinan] = kopya.splice(yer, 1)
    kopya.splice(hedef, 0, tasinan)
    bloklariDegistir(kopya)
  }

  const secili = bloklar.find((b) => b.id === seciliBlok) ?? null
  const seciliTanim = katalog.data?.find((t) => t.type === secili?.type)
  const otomatik = aktifSayfa?.kind === 'auto'

  if (site.isError) {
    return (
      <div className="p-6 text-sm">
        Düzenleyici açılamadı. <Link to="/web-sitesi">Web Sitesi</Link> ekranına dönün.
      </div>
    )
  }

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="flex flex-wrap items-center gap-3 border-b border-[var(--ad-border)] bg-[var(--ad-surface)] px-4 py-2.5">
        <Button size="sm" variant="ghost" asChild>
          <Link to="/web-sitesi">
            <Icon name="panel-left" size={16} aria-hidden /> Panele dön
          </Link>
        </Button>

        <nav className="flex flex-wrap items-center gap-1">
          {sayfalar.map((sayfa) => (
            <button
              key={sayfa.id}
              type="button"
              onClick={() => {
                yuklenenSayfa.current = null
                setAktifSayfaId(sayfa.id)
              }}
              className={`flex items-center gap-1.5 rounded-[var(--radius-control)] px-3 py-1.5 text-sm ${
                sayfa.id === aktifSayfa?.id
                  ? 'bg-[var(--ad-surface-muted)] font-medium'
                  : 'text-[var(--ad-text-muted)]'
              }`}
            >
              {sayfa.title}
              {sayfa.has_unpublished_changes && (
                <span className="size-1.5 rounded-full bg-[var(--ad-warning)]" />
              )}
            </button>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2 text-xs text-[var(--ad-text-muted)]">
          {kaydet.isPending
            ? 'Kaydediliyor…'
            : kirli
              ? 'Kaydedilmemiş değişiklik'
              : sonKayit
                ? `Kaydedildi · ${sonKayit.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}`
                : ''}
          <Button size="sm" variant="secondary" asChild>
            <a href={site.data?.site_url} target="_blank" rel="noreferrer">
              Önizle
            </a>
          </Button>
          <Button size="sm" disabled={yayinla.isPending} onClick={() => yayinla.mutate()}>
            {yayinla.isPending ? 'Yayınlanıyor…' : 'Yayınla'}
          </Button>
        </div>
      </header>

      <div className="grid flex-1 grid-cols-[15rem_1fr_20rem]">
        <aside className="border-r border-[var(--ad-border)] p-3">
          <p className="pb-2 text-xs font-medium uppercase tracking-wide text-[var(--ad-text-muted)]">
            Bloklar
          </p>
          {otomatik ? (
            <p className="text-xs text-[var(--ad-text-muted)]">
              Bu sayfanın içeriği modülden geliyor; blok eklenmez.
            </p>
          ) : (
            ['basic', 'association', 'other'].map((grup) => (
              <div key={grup} className="pb-4">
                <p className="pb-1.5 text-[11px] font-medium uppercase tracking-wide text-[var(--ad-text-muted)]">
                  {GRUP_ADI[grup]}
                </p>
                <div className="grid grid-cols-2 gap-1.5">
                  {(katalog.data ?? [])
                    .filter((tanim) => tanim.group === grup)
                    .map((tanim) => (
                      <button
                        key={tanim.type}
                        type="button"
                        onClick={() => blokEkle(tanim.type)}
                        className="rounded-[var(--radius-control)] border border-[var(--ad-border)] px-2 py-2 text-left text-xs hover:border-[var(--ad-primary)]"
                      >
                        {tanim.label}
                      </button>
                    ))}
                </div>
              </div>
            ))
          )}
        </aside>

        <main className="bg-[var(--ad-surface-muted)] p-6">
          {otomatik ? (
            <div className="mx-auto max-w-2xl rounded-[var(--radius-card)] border border-dashed border-[var(--ad-border)] bg-[var(--ad-surface)] p-8 text-center text-sm">
              <p className="font-medium">{aktifSayfa?.title}</p>
              <p className="pt-2 text-[var(--ad-text-muted)]">
                {DINAMIK_ACIKLAMA[aktifSayfa?.auto_source ?? ''] ??
                  'İçerik ilgili modülden geliyor.'}
              </p>
            </div>
          ) : (
            <div className="mx-auto flex max-w-2xl flex-col gap-2">
              {bloklar.length === 0 && (
                <p className="rounded-[var(--radius-card)] border border-dashed border-[var(--ad-border)] bg-[var(--ad-surface)] p-8 text-center text-sm text-[var(--ad-text-muted)]">
                  Soldaki listeden blok ekleyin.
                </p>
              )}
              {bloklar.map((blok, sira) => {
                const tanim = katalog.data?.find((t) => t.type === blok.type)
                const baslik =
                  (blok.data.title as string) || (blok.data.body as string) || tanim?.label
                return (
                  <article
                    key={blok.id}
                    onClick={() => setSeciliBlok(blok.id)}
                    className={`cursor-pointer rounded-[var(--radius-card)] border bg-[var(--ad-surface)] p-4 ${
                      blok.id === seciliBlok
                        ? 'border-[var(--ad-primary)]'
                        : 'border-[var(--ad-border)]'
                    } ${blok.hidden ? 'opacity-50' : ''}`}
                  >
                    <div className="flex items-center gap-2">
                      <Badge tone="neutral">{tanim?.label ?? blok.type}</Badge>
                      {blok.hidden && <Badge tone="warning">Gizli</Badge>}
                      <span className="ml-auto flex items-center gap-1">
                        <button
                          type="button"
                          aria-label="Yukarı taşı"
                          disabled={sira === 0}
                          onClick={(e) => {
                            e.stopPropagation()
                            blokTasi(blok.id, -1)
                          }}
                          className="px-1 text-xs text-[var(--ad-text-muted)] disabled:opacity-30"
                        >
                          ▲
                        </button>
                        <button
                          type="button"
                          aria-label="Aşağı taşı"
                          disabled={sira === bloklar.length - 1}
                          onClick={(e) => {
                            e.stopPropagation()
                            blokTasi(blok.id, 1)
                          }}
                          className="px-1 text-xs text-[var(--ad-text-muted)] disabled:opacity-30"
                        >
                          ▼
                        </button>
                        <button
                          type="button"
                          aria-label={blok.hidden ? 'Göster' : 'Gizle'}
                          onClick={(e) => {
                            e.stopPropagation()
                            bloklariDegistir(
                              bloklar.map((b) =>
                                b.id === blok.id ? { ...b, hidden: !b.hidden } : b,
                              ),
                            )
                          }}
                          className="px-1"
                        >
                          <Icon name={blok.hidden ? 'eye-off' : 'eye'} size={16} aria-hidden />
                        </button>
                        <button
                          type="button"
                          aria-label="Sil"
                          onClick={(e) => {
                            e.stopPropagation()
                            bloklariDegistir(bloklar.filter((b) => b.id !== blok.id))
                            if (seciliBlok === blok.id) setSeciliBlok(null)
                          }}
                          className="px-1"
                        >
                          <Icon name="trash-2" size={16} aria-hidden />
                        </button>
                      </span>
                    </div>
                    <p className="truncate pt-2 text-sm">{baslik}</p>
                  </article>
                )
              })}
            </div>
          )}
        </main>

        <aside className="flex flex-col gap-3 border-l border-[var(--ad-border)] p-4">
          <div
            role="tablist"
            className="flex gap-1 rounded-[var(--radius-control)] bg-[var(--ad-surface-muted)] p-1 text-xs"
          >
            {(
              [
                ['blok', 'Blok'],
                ['tema', 'Tema'],
                ['menu', 'Menü'],
                ['seo', 'SEO'],
              ] as const
            ).map(([kod, etiket]) => (
              <button
                key={kod}
                role="tab"
                aria-selected={sekme === kod}
                onClick={() => setSekme(kod)}
                className={`flex-1 rounded-[var(--radius-control)] px-2 py-1.5 ${
                  sekme === kod
                    ? 'bg-[var(--ad-surface)] font-medium shadow-sm'
                    : 'text-[var(--ad-text-muted)]'
                }`}
              >
                {etiket}
              </button>
            ))}
          </div>

          {sekme === 'tema' && site.data && (
            <TemaSekmesi
              ayarlar={site.data.settings}
              paletler={site.data.palettes}
              yaziTipleri={site.data.font_pairings}
              onDegis={(govde) => temaKaydet.mutate(govde)}
            />
          )}

          {sekme === 'menu' && (
            <MenuSekmesi
              sayfalar={sayfalar}
              onDegis={(id, in_menu) => sayfaKaydet.mutate({ id, in_menu })}
            />
          )}

          {sekme === 'seo' && aktifSayfa && (
            <SeoSekmesi
              key={aktifSayfa.id}
              sayfa={aktifSayfa}
              onKaydet={(govde) => sayfaKaydet.mutate({ id: aktifSayfa.id, ...govde })}
            />
          )}

          {sekme === 'blok' &&
            (secili ? (
              <>
                <p className="text-sm font-medium">{seciliTanim?.label ?? secili.type}</p>
                {seciliTanim?.dynamic && (
                  <p className="text-xs text-[var(--ad-text-muted)]">
                    {DINAMIK_ACIKLAMA[secili.type]}
                  </p>
                )}
                <AlanFormu
                  alanlar={
                    seciliTanim?.dynamic ? DINAMIK_ALANLAR : (BLOK_ALANLARI[secili.type] ?? [])
                  }
                  veri={secili.data}
                  onDegis={(veri) => blokGuncelle(secili.id, veri)}
                />
              </>
            ) : (
              <p className="text-sm text-[var(--ad-text-muted)]">Düzenlemek için bir blok seçin.</p>
            ))}
        </aside>
      </div>
    </div>
  )
}

/**
 * Yeni bloğa geçici kimlik. Gerçek kimliği sunucu veriyor
 * (`normalize_blocks`); buradaki tek iş, tuvalde iki bloğun aynı React
 * anahtarını almaması. Saat ya da rastgelelik yerine mevcut listeden
 * türetiliyor: render saf kalsın.
 */
function gecicikimlik(mevcut: Blok[]): string {
  let sira = mevcut.length + 1
  while (mevcut.some((blok) => blok.id === `yeni-${sira}`)) sira += 1
  return `yeni-${sira}`
}

/** Alan tanımlarından form. Yeni bir blok, yeni bir ekran değil, yeni bir satır. */
function AlanFormu({
  alanlar,
  veri,
  onDegis,
}: {
  alanlar: Alan[]
  veri: Record<string, unknown>
  onDegis: (veri: Record<string, unknown>) => void
}) {
  if (alanlar.length === 0) {
    return <p className="text-sm text-[var(--ad-text-muted)]">Bu bloğun ayarı yok.</p>
  }

  return (
    <div className="flex flex-col gap-3">
      {alanlar.map((alan) => {
        if (alan.tur === 'liste') {
          const ogeler = Array.isArray(veri[alan.ad])
            ? (veri[alan.ad] as Record<string, unknown>[])
            : []
          return (
            <div key={alan.ad} className="flex flex-col gap-2">
              <p className="text-xs font-medium uppercase tracking-wide text-[var(--ad-text-muted)]">
                {alan.etiket}
              </p>
              {ogeler.map((oge, sira) => (
                <div
                  key={sira}
                  className="flex flex-col gap-2 rounded-[var(--radius-control)] border border-[var(--ad-border)] p-2"
                >
                  <AlanFormu
                    alanlar={alan.alanlar}
                    veri={oge}
                    onDegis={(yeni) =>
                      onDegis({
                        ...veri,
                        [alan.ad]: ogeler.map((o, i) => (i === sira ? yeni : o)),
                      })
                    }
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      onDegis({ ...veri, [alan.ad]: ogeler.filter((_, i) => i !== sira) })
                    }
                  >
                    {alan.tekil} sil
                  </Button>
                </div>
              ))}
              <Button
                size="sm"
                variant="secondary"
                onClick={() => onDegis({ ...veri, [alan.ad]: [...ogeler, {}] })}
              >
                {alan.tekil} ekle
              </Button>
            </div>
          )
        }

        const deger = typeof veri[alan.ad] === 'string' ? (veri[alan.ad] as string) : ''
        const yaz = (yeni: string) => onDegis({ ...veri, [alan.ad]: yeni })

        return (
          <Field
            key={alan.ad}
            label={alan.etiket}
            hint={alan.tur === 'secim' ? undefined : alan.ipucu}
          >
            {({ id }) =>
              alan.tur === 'uzunMetin' ? (
                <textarea
                  id={id}
                  rows={4}
                  value={deger}
                  placeholder={alan.yer}
                  onChange={(event) => yaz(event.target.value)}
                  className="w-full rounded-[var(--radius-control)] border border-[var(--ad-border)] bg-[var(--ad-surface)] p-2 text-sm"
                />
              ) : alan.tur === 'secim' ? (
                <select
                  id={id}
                  value={deger || alan.secenekler[0][0]}
                  onChange={(event) => yaz(event.target.value)}
                  className={girdiSinifi}
                >
                  {alan.secenekler.map(([kod, etiket]) => (
                    <option key={kod} value={kod}>
                      {etiket}
                    </option>
                  ))}
                </select>
              ) : (
                <Input
                  id={id}
                  value={deger}
                  placeholder={alan.yer}
                  onChange={(event) => yaz(event.target.value)}
                />
              )
            }
          </Field>
        )
      })}
    </div>
  )
}

/**
 * Tema sekmesi — Figma `Düzenleyici · Tema`.
 *
 * Hazır palet seçmek dört rengi birden yazıyor; renkler kayıtta çözülmüş
 * duruyor (content/models.py), yani palet tanımı kodda değişse bile yayındaki
 * site kendiliğinden değişmiyor. "Özel" bir palet değil, paletsizlik: dernek
 * renkleri kendi giriyor.
 */
function TemaSekmesi({
  ayarlar,
  paletler,
  yaziTipleri,
  onDegis,
}: {
  ayarlar: Ayarlar
  paletler: Record<string, string[]>
  yaziTipleri: Record<string, string[]>
  onDegis: (govde: Record<string, unknown>) => void
}) {
  const renkAlanlari = [
    ['primary_color', 'Ana renk'],
    ['accent_color', 'Vurgu'],
    ['surface_color', 'Zemin'],
    ['text_color', 'Metin'],
  ] as const

  return (
    <div className="flex flex-col gap-4 text-sm">
      <Field label="Logo adresi" hint="Boşsa derneğin baş harfi görünür.">
        {({ id }) => (
          <Input
            id={id}
            defaultValue={ayarlar.logo_url ?? ''}
            onBlur={(event) => onDegis({ logo_url: event.target.value.trim() || null })}
          />
        )}
      </Field>

      <div className="flex flex-col gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-[var(--ad-text-muted)]">
          Renk paleti
        </p>
        <div className="grid grid-cols-3 gap-1.5">
          {Object.entries(paletler).map(([ad, renkler]) => (
            <button
              key={ad}
              type="button"
              onClick={() => onDegis({ palette: ad })}
              className={`flex flex-col gap-1 rounded-[var(--radius-control)] border p-1.5 ${
                ayarlar.palette === ad ? 'border-[var(--ad-primary)]' : 'border-[var(--ad-border)]'
              }`}
            >
              <span className="flex h-4 overflow-hidden rounded-sm">
                {renkler.slice(0, 3).map((renk) => (
                  <span key={renk} className="flex-1" style={{ background: renk }} />
                ))}
              </span>
              <span className="text-[11px] capitalize">{ad}</span>
            </button>
          ))}
        </div>
        {renkAlanlari.map(([alan, etiket]) => (
          <label key={alan} className="flex items-center gap-2 text-xs">
            <input
              type="color"
              value={ayarlar[alan]}
              onChange={(event) => onDegis({ palette: 'ozel', [alan]: event.target.value })}
              className="size-7 rounded border border-[var(--ad-border)] bg-transparent"
            />
            <span className="flex-1">{etiket}</span>
            <span className="font-mono text-[var(--ad-text-muted)]">{ayarlar[alan]}</span>
          </label>
        ))}
      </div>

      <div className="flex flex-col gap-1.5">
        <p className="text-xs font-medium uppercase tracking-wide text-[var(--ad-text-muted)]">
          Yazı tipi
        </p>
        {Object.entries(yaziTipleri).map(([ad, fontlar]) => (
          <button
            key={ad}
            type="button"
            onClick={() => onDegis({ font_pairing: ad })}
            className={`flex items-center justify-between rounded-[var(--radius-control)] border px-3 py-2 text-left ${
              ayarlar.font_pairing === ad
                ? 'border-[var(--ad-primary)]'
                : 'border-[var(--ad-border)]'
            }`}
          >
            <span>
              <span className="block text-sm capitalize">{ad}</span>
              <span className="block text-[11px] text-[var(--ad-text-muted)]">
                {fontlar.join(' + ')}
              </span>
            </span>
            {ayarlar.font_pairing === ad && (
              <Icon name="check" size={16} primaryColor="var(--ad-primary)" aria-hidden />
            )}
          </button>
        ))}
      </div>

      <SecimSatiri
        etiket="Köşeler"
        secili={ayarlar.corner_style}
        secenekler={[
          ['keskin', 'Keskin'],
          ['yumusak', 'Yumuşak'],
          ['yuvarlak', 'Yuvarlak'],
        ]}
        onSec={(deger) => onDegis({ corner_style: deger })}
      />
      <SecimSatiri
        etiket="Üst menü"
        secili={ayarlar.header_style}
        secenekler={[
          ['beyaz', 'Beyaz'],
          ['renkli', 'Renkli'],
          ['koyu', 'Koyu'],
          ['seffaf', 'Şeffaf'],
        ]}
        onSec={(deger) => onDegis({ header_style: deger })}
      />

      <Button size="sm" variant="ghost" onClick={() => onDegis({ template: ayarlar.template })}>
        Şablon varsayılanına dön
      </Button>
    </div>
  )
}

function SecimSatiri({
  etiket,
  secili,
  secenekler,
  onSec,
}: {
  etiket: string
  secili: string
  secenekler: [string, string][]
  onSec: (deger: string) => void
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-xs font-medium uppercase tracking-wide text-[var(--ad-text-muted)]">
        {etiket}
      </p>
      <div className="flex gap-1.5">
        {secenekler.map(([kod, ad]) => (
          <button
            key={kod}
            type="button"
            onClick={() => onSec(kod)}
            className={`flex-1 rounded-[var(--radius-control)] border px-2 py-1.5 text-xs ${
              secili === kod ? 'border-[var(--ad-primary)]' : 'border-[var(--ad-border)]'
            }`}
          >
            {ad}
          </button>
        ))}
      </div>
    </div>
  )
}

/**
 * Menü sekmesi. Menü öğeleri sayfalarla senkron: yayından kaldırılan sayfa
 * menüden kendiliğinden düşer (sunucu yalnızca yayınlanmış sayfaları
 * döndürüyor), o yüzden burada ayrı bir menü listesi tutulmuyor.
 */
function MenuSekmesi({
  sayfalar,
  onDegis,
}: {
  sayfalar: Sayfa[]
  onDegis: (id: string, inMenu: boolean) => void
}) {
  return (
    <div className="flex flex-col gap-2 text-sm">
      <p className="text-xs text-[var(--ad-text-muted)]">
        Sıra, Web Sitesi ekranındaki sıradan gelir. Yayında olmayan sayfa menüde görünmez.
      </p>
      {sayfalar.map((sayfa) => (
        <label
          key={sayfa.id}
          className="flex items-center gap-2 rounded-[var(--radius-control)] border border-[var(--ad-border)] px-3 py-2"
        >
          <input
            type="checkbox"
            checked={sayfa.in_menu}
            onChange={(event) => onDegis(sayfa.id, event.target.checked)}
          />
          <span className="min-w-0 flex-1">
            <span className="block truncate">{sayfa.title}</span>
            <span className="block text-[11px] text-[var(--ad-text-muted)]">
              /{sayfa.slug} · {sayfa.status === 'published' ? 'yayında' : 'taslak'}
            </span>
          </span>
          <Icon name={sayfa.in_menu ? 'eye' : 'eye-off'} size={16} aria-hidden />
        </label>
      ))}
    </div>
  )
}

/** SEO sekmesi: arama sonucunda ve paylaşımda görünen metinler. */
function SeoSekmesi({
  sayfa,
  onKaydet,
}: {
  sayfa: Sayfa
  onKaydet: (govde: Record<string, unknown>) => void
}) {
  return (
    <div className="flex flex-col gap-3 text-sm">
      <Field label="Sayfa adresi" hint="Değiştirirseniz eski bağlantılar kırılır.">
        {({ id }) => (
          <Input
            id={id}
            defaultValue={sayfa.slug}
            disabled={sayfa.kind === 'home'}
            onBlur={(event) => {
              const yeni = event.target.value.trim()
              if (yeni !== sayfa.slug) onKaydet({ slug: yeni })
            }}
          />
        )}
      </Field>
      <Field label="Arama başlığı" hint="Boşsa sayfa başlığı kullanılır.">
        {({ id }) => (
          <Input
            id={id}
            defaultValue={sayfa.seo_title ?? ''}
            onBlur={(event) => onKaydet({ seo_title: event.target.value.trim() || null })}
          />
        )}
      </Field>
      <Field label="Arama açıklaması" hint="Arama sonucunda görünen iki satır.">
        {({ id }) => (
          <textarea
            id={id}
            rows={3}
            defaultValue={sayfa.seo_description ?? ''}
            onBlur={(event) => onKaydet({ seo_description: event.target.value.trim() || null })}
            className="w-full rounded-[var(--radius-control)] border border-[var(--ad-border)] bg-[var(--ad-surface)] p-2 text-sm"
          />
        )}
      </Field>
      <Field label="Paylaşım görseli" hint="Bağlantı paylaşıldığında görünür.">
        {({ id }) => (
          <Input
            id={id}
            defaultValue={sayfa.og_image_url ?? ''}
            onBlur={(event) => onKaydet({ og_image_url: event.target.value.trim() || null })}
          />
        )}
      </Field>
    </div>
  )
}
